<template>
    <div class="monitoring-bar" ref="root">
        <button type="button"
            class="monitor-btn"
            :class="[`is-${worstSeverity}`, { 'is-open': isOpen }]"
            :aria-label="`Monitoramento: ${totalGroups} alerta(s)${hasCritical ? ', há alerta crítico' : ''}`"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-busy="worstSeverity === 'loading' ? 'true' : 'false'"
            @click="toggle()">
            <span v-if="totalGroups > 0"
                class="monitor-badge"
                :class="{ 'is-new': animateBadge }">{{ totalGroups }}</span>
            <span v-else-if="worstSeverity === 'ok'" class="monitor-dot" />
            <span v-else-if="worstSeverity === 'loading'" class="monitor-spinner" />
            <span v-else-if="worstSeverity === 'error'" class="monitor-error">!</span>

            <svg class="monitor-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="1.75" y="3.25" width="16.5" height="11.5" rx="2"
                    stroke="currentColor" stroke-width="1.5" />
                <path class="monitor-trace-rail"
                    d="M4.5 9.5h2.2l1.5-3 2 5.5 1.6-3.4 1 .9h2.7"
                    stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" />
                <path class="monitor-trace"
                    d="M4.5 9.5h2.2l1.5-3 2 5.5 1.6-3.4 1 .9h2.7"
                    pathLength="1"
                    stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" />
                <line x1="7" y1="17.25" x2="13" y2="17.25"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <span class="monitor-label">Monitoramento</span>
        </button>

        <div class="mp" v-if="isOpen">
            <header class="mp-header" :class="`is-${worstSeverity}`">
                <span class="mp-stripe" />
                <div>
                    <p class="mp-title">Central de Monitoramento</p>
                    <p class="mp-subtitle">{{ summary }}</p>
                </div>
            </header>

            <div class="mp-list">
                <p class="mp-empty" v-if="worstSeverity === 'loading'">
                    <span class="monitor-spinner" />
                    Verificando serviços...
                </p>

                <div class="mp-empty" v-else-if="worstSeverity === 'error'">
                    <p>Não foi possível verificar agora.</p>
                    <button type="button" class="btn btn-outline-secondary btn-sm mt-2"
                        :disabled="loading"
                        @click="load()">
                        Tentar novamente
                    </button>
                </div>

                <div class="mp-all-good" v-else-if="worstSeverity === 'ok'">
                    <span class="mp-all-good-icon">
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </span>
                    <p class="mp-all-good-title">Tudo certo por aqui</p>
                    <p class="mp-all-good-text">Nenhum serviço precisa de atenção no momento.</p>
                    <p class="mp-all-good-time">Verificado às {{ loadedAtLabel }}</p>
                </div>

                <div v-for="group in groups" :key="group.key" class="mp-group">
                    <div class="mp-item"
                        :class="{ 'mp-item-link': isGroupClickable(group), 'is-expanded': isExpanded(group) }"
                        @click="onGroupClick(group)">
                        <span class="mp-sev" :class="`mp-sev-${group.severityClass}`" />

                        <div class="mp-info">
                            <span class="mp-label" v-html="group.title"></span>
                            <span class="mp-desc" v-if="group.isSingle()">
                                <ChannelLogoCmp v-if="channelOf(group.first)" :channel="channelOf(group.first)"
                                    :size="14" :show-name="false" class="mp-channel" />{{ group.first.message }}
                            </span>
                            <span class="mp-desc" v-else>
                                <span v-if="groupBrands(group).length" class="mp-brand-stack" aria-hidden="true">
                                    <ChannelLogoCmp v-for="c in groupBrands(group).slice(0, brandStackLimit)"
                                        :key="`stack-${c.enumChannel}`"
                                        :channel="c" :size="16" :show-name="false" />
                                    <span v-if="groupBrands(group).length > brandStackLimit" class="mp-brand-stack-more">
                                        +{{ groupBrands(group).length - brandStackLimit }}
                                    </span>
                                </span>
                                {{ group.count }} ocorrências
                            </span>

                            <span v-if="group.isSingle() && group.first.hasReadConfirmation" class="mp-item-actions">
                                <button type="button" class="btn btn-outline-secondary btn-sm"
                                    @click.stop="confirmReadStatus(group.first)">
                                    Ciente
                                </button>
                            </span>
                        </div>

                        <span v-if="!group.isSingle()" class="mp-tag" :class="`mp-tag-${group.severityClass}`">
                            {{ group.count }}
                            <fa icon="chevron-down" class="mp-tag-chevron" />
                        </span>
                        <span v-else-if="group.first.hasLink()" class="mp-tag" :class="`mp-tag-${group.severityClass}`">
                            <fa :icon="group.first.isLinkExternal() ? 'external-link-alt' : 'chevron-right'" />
                        </span>
                    </div>

                    <div class="mp-subitems" v-if="isExpanded(group)">
                        <div class="mp-subitem" v-for="(info, index) in group.items" :key="index">
                            <span class="mp-subitem-message">
                                <ChannelLogoCmp v-if="channelOf(info)" :channel="channelOf(info)"
                                    :size="16" :show-name="false" class="mp-channel" />
                                <span>{{ info.message }}</span>
                            </span>

                            <span class="mp-subitem-actions">
                                <template v-if="info.hasLink()">
                                    <router-link v-if="!info.isLinkExternal()"
                                        :to="info.link"
                                        class="btn btn-outline-secondary btn-sm">
                                        Detalhe
                                    </router-link>
                                    <a v-else
                                        :href="info.link"
                                        target="_blank"
                                        class="btn btn-outline-secondary btn-sm"
                                        @click="close()">
                                        Detalhe
                                        <fa icon="external-link-alt" class="ml-1" />
                                    </a>
                                </template>

                                <button v-if="info.hasReadConfirmation"
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm ml-2"
                                    @click="confirmReadStatus(info)">
                                    Ciente
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <footer class="mp-foot">
                <span v-if="loadedAt">Atualizado às {{ loadedAtLabel }}</span>
                <span v-else>Conectando ao monitoramento...</span>

                <a class="mp-foot-link" @click="openFullMonitoring()">
                    Monitoramento completo
                </a>
            </footer>
        </div>

        <MonitoringCmp ref="monitoringCmp" />
    </div>
</template>

<script>
import serviceStatusService from '@/monitoring/services/ServiceStatusInformationService'
import MonitoringCmp from '@/monitoring/components/MonitoringCmp'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import channelService from '@/channel/services/ChannelService'
import { groupStatusInformation, worstSeverityClass, countBySeverity } from '@/monitoring/models/StatusInformationGroupModel'

const REFRESH_INTERVAL_MS = 5 * 60 * 1000
const BADGE_POP_MS = 400
const BRAND_STACK_LIMIT = 5

// A animação do badge marca dado NOVO, não montagem de componente. Guardar o
// último total no escopo do módulo faz ela disparar só quando o número muda.
let lastAnimatedTotal = null

export default {
    name: 'MonitoringBarCmp',
    components: {
        MonitoringCmp,
        ChannelLogoCmp,
    },
    data() {
        return {
            statusesInformation: [],
            channelsById: {},
            brandStackLimit: BRAND_STACK_LIMIT,
            isOpen: false,
            expandedKey: null,
            loading: false,
            hasError: false,
            loadedAt: null,
            animateBadge: false,
        }
    },
    mounted() {
        this.load()
        this.refreshTimer = setInterval(() => this.load(), REFRESH_INTERVAL_MS)

        document.addEventListener('click', this.onDocumentClick, true)
        document.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        clearInterval(this.refreshTimer)
        clearTimeout(this.badgeTimer)

        document.removeEventListener('click', this.onDocumentClick, true)
        document.removeEventListener('keydown', this.onKeydown)
    },
    watch: {
        $route() {
            this.close()
        },
        totalGroups(total) {
            if (lastAnimatedTotal !== null && total !== lastAnimatedTotal && total > 0) {
                this.animateBadge = true
                clearTimeout(this.badgeTimer)
                this.badgeTimer = setTimeout(() => { this.animateBadge = false }, BADGE_POP_MS)
            }

            lastAnimatedTotal = total
        },
    },
    methods: {
        async load() {
            this.loading = true

            try {
                this.statusesInformation = await serviceStatusService.getServiceStatusInformation()
                this.hasError = false
                this.loadedAt = new Date()

                // Primeira carga da sessão: registra o total sem animar.
                if (lastAnimatedTotal === null) {
                    lastAnimatedTotal = this.totalGroups
                }
            } catch (e) {
                this.hasError = true
            }

            this.loading = false
            this.loadChannels()
        },
        // As marcas são só decoração: sem a lista de canais, as linhas ficam sem ícone.
        async loadChannels() {
            const hasUnknownChannel = this.statusesInformation.some((s) => {
                const id = s.getChannelId()
                return id && !this.channelsById[id]
            })

            if (!hasUnknownChannel || this.loadingChannels) {
                return
            }

            this.loadingChannels = true

            try {
                const channels = await channelService.getChannels()
                const byId = {}

                for (const channel of channels) {
                    byId[String(channel.id)] = channel
                }

                this.channelsById = byId
            } catch (e) {
                // Sem ícones, nada mais muda.
            }

            this.loadingChannels = false
        },
        channelOf(info) {
            const id = info.getChannelId()
            return id ? this.channelsById[id] : null
        },
        // Uma marca por tipo de canal: três Shopees viram um ícone só na pilha.
        groupBrands(group) {
            const brands = []
            const seen = {}

            for (const info of group.items) {
                const channel = this.channelOf(info)

                if (channel && !seen[channel.enumChannel]) {
                    seen[channel.enumChannel] = true
                    brands.push(channel)
                }
            }

            return brands
        },
        toggle() {
            if (this.isOpen) {
                this.close()
                return
            }

            this.isOpen = true
            this.load()
        },
        close() {
            this.isOpen = false
            this.expandedKey = null
        },
        onDocumentClick(e) {
            if (this.isOpen && this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.close()
            }
        },
        onKeydown(e) {
            if (this.isOpen && e.key === 'Escape') {
                this.close()
            }
        },
        isExpanded(group) {
            return this.expandedKey === group.key
        },
        isGroupClickable(group) {
            return !group.isSingle() || group.first.hasLink()
        },
        onGroupClick(group) {
            if (!group.isSingle()) {
                this.expandedKey = this.isExpanded(group) ? null : group.key
                return
            }

            const info = group.first

            if (!info.hasLink()) {
                return
            }

            if (info.isLinkExternal()) {
                window.open(info.link, '_blank')
                this.close()
                return
            }

            if (this.$route.fullPath === info.link) {
                this.close()
                return
            }

            this.$router.push(info.link)
        },
        async confirmReadStatus(info) {
            try {
                await serviceStatusService.confirmReadStatus(info.id)
                this.statusesInformation = this.statusesInformation.filter(s => s.id !== info.id)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }
        },
        // O modal abre na hora; a Central passa a lista que acabou de buscar para a aba Geral já nascer pronta.
        openFullMonitoring() {
            this.close()
            this.$refs.monitoringCmp.show(this.loadedAt ? this.statusesInformation : undefined)
        },
    },
    computed: {
        groups() {
            return groupStatusInformation(this.statusesInformation)
        },
        totalGroups() {
            return this.groups.length
        },
        hasCritical() {
            return this.worstSeverity === 'critical'
        },
        worstSeverity() {
            if (!this.loadedAt) {
                return this.hasError ? 'error' : 'loading'
            }

            return worstSeverityClass(this.groups)
        },
        summary() {
            if (this.worstSeverity === 'loading') {
                return 'Verificando serviços...'
            }

            if (this.worstSeverity === 'error') {
                return 'Status indisponível no momento'
            }

            if (!this.groups.length) {
                return 'Todos os serviços operando normalmente'
            }

            const count = countBySeverity(this.groups)
            const parts = []

            if (count.critical) {
                parts.push(`${count.critical} ${count.critical === 1 ? 'crítico' : 'críticos'}`)
            }

            if (count.warning) {
                parts.push(`${count.warning} ${count.warning === 1 ? 'aviso' : 'avisos'}`)
            }

            if (count.info) {
                parts.push(`${count.info} ${count.info === 1 ? 'informativo' : 'informativos'}`)
            }

            return parts.join(' · ')
        },
        loadedAtLabel() {
            return this.loadedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        },
    },
}
</script>

<style lang="scss" scoped>
$critical: var(--monitor-critical);
$warning: var(--monitor-warning);
$info: var(--monitor-info);
$ok: var(--success-solid);
$brand: var(--brand);
$border: var(--tint-brand-border);

.monitoring-bar {
    position: relative;
}

.monitor-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 44px;
    padding: 0 16px 0 14px;
    background-color: var(--surface-2);
    border: 1px solid transparent;
    border-radius: 999px;
    color: var(--text-muted);
    font-weight: 600;
    font-size: .9em;
    line-height: 1;
    cursor: pointer;
    transition: background-color .15s ease, box-shadow .15s ease, color .15s ease;

    &:hover,
    &.is-open {
        box-shadow: 0 1px 3px color-mix(in srgb, var(--shadow-ink) 6%, transparent);
    }

    &:focus {
        outline: none;
    }

    &.is-critical {
        background-color: var(--danger-bg);
        border-color: var(--monitor-critical-border);
        color: var(--monitor-critical-fg);
    }

    &.is-warning {
        background-color: var(--warning-bg);
        border-color: var(--monitor-warning-border);
        color: var(--monitor-warning-fg);
    }

    &.is-info {
        background-color: var(--info-bg);
        border-color: var(--monitor-info-border);
        color: var(--monitor-info-fg);
    }

    &.is-ok {
        background-color: var(--success-bg);
        border-color: var(--success-border);
        color: var(--success-fg);

        &:hover,
        &.is-open {
            background-color: var(--success-bg);
        }
    }

    &.is-loading,
    &.is-error {
        color: var(--text-faint);

        &:hover,
        &.is-open {
            background-color: var(--tint-brand-hover);
        }
    }

    .monitor-spinner {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        border: 1.5px solid var(--border);
        border-top-color: var(--border-strong);
        flex-shrink: 0;
        animation: spin .8s linear infinite;
    }

    .monitor-error {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background-color: var(--tint-brand-border);
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .monitor-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    .monitor-label {
        white-space: nowrap;
    }

    .monitor-badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: 9px;
        background-color: $info;
        color: var(--text-on-brand);
        font-size: 10px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 0 2px var(--surface-1);

        &.is-new {
            animation: badge-pop .35s cubic-bezier(.34, 1.56, .64, 1);
        }
    }

    &.is-critical .monitor-badge {
        background-color: $critical;
        box-shadow: 0 0 0 2px var(--surface-1), 0 0 8px 1px color-mix(in srgb, #{$critical} 35%, transparent);
    }
    &.is-warning .monitor-badge { background-color: $warning; }

    // Fora do crítico o trilho some e o traço é a linha do ícone.
    .monitor-trace-rail {
        opacity: 0;
    }

    // Crítico: a linha do monitor "dispara" como um ECG e um halo pulsa em volta.
    &.is-critical {
        &::after {
            content: '';
            position: absolute;
            top: -1px;
            right: -1px;
            bottom: -1px;
            left: -1px;
            border-radius: 999px;
            pointer-events: none;
            animation: critical-ring 2s ease-out infinite;
        }

        .monitor-trace-rail {
            opacity: .25;
        }

        .monitor-trace {
            stroke-dasharray: 1;
            animation: ecg-trace 1.4s linear infinite;
        }

        // Com a Central aberta o usuário já está olhando: acalma.
        &.is-open {
            &::after,
            .monitor-trace {
                animation: none;
            }

            .monitor-trace-rail {
                opacity: 0;
            }
        }
    }

    .monitor-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: $ok;
        flex-shrink: 0;
        animation: pulse-ok 2.4s ease-out infinite;
    }
}

@keyframes badge-pop {
    from { transform: scale(.4); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}

@keyframes ecg-trace {
    from { stroke-dashoffset: 1; }
    to { stroke-dashoffset: -1; }
}

@keyframes critical-ring {
    0% { box-shadow: 0 0 0 0 color-mix(in srgb, #{$critical} 45%, transparent); }
    100% { box-shadow: 0 0 0 9px transparent; }
}

@media (prefers-reduced-motion: reduce) {
    .monitor-btn {
        &.is-critical {
            border: 2px solid $critical;

            &::after,
            .monitor-trace {
                animation: none;
            }

            .monitor-trace {
                stroke-dasharray: none;
            }

            .monitor-trace-rail {
                opacity: 0;
            }
        }

        .monitor-badge.is-new,
        .monitor-dot {
            animation: none;
        }
    }

    .mp .mp-all-good .mp-all-good-icon {
        animation: none;
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

@keyframes check-pop {
    from { transform: scale(.6); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}

@keyframes pulse-ok {
    0% { box-shadow: 0 0 0 0 color-mix(in srgb, #{$ok} 50%, transparent); }
    70% { box-shadow: 0 0 0 6px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
}

.mp {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    width: 400px;
    background-color: var(--surface-overlay);
    border: 1px solid $border;
    border-radius: 8px;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--shadow-ink) 12%, transparent);
    z-index: 100000;
    overflow: hidden;

    p {
        margin-bottom: 0;
    }

    .mp-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background-color: var(--tint-brand-soft);
        border-bottom: 1px solid $border;

        .mp-stripe {
            width: 3px;
            align-self: stretch;
            border-radius: 3px;
            background-color: $brand;
        }

        &.is-critical .mp-stripe { background-color: $critical; }
        &.is-warning .mp-stripe { background-color: $warning; }
        &.is-info .mp-stripe { background-color: $info; }
        &.is-ok .mp-stripe { background-color: $ok; }

        .mp-title {
            font-size: .95em;
            font-weight: 700;
            color: $brand;
        }

        .mp-subtitle {
            margin-top: 2px;
            font-size: .78em;
            color: var(--text-faint);
        }
    }

    .mp-list {
        max-height: 60vh;
        overflow-y: auto;
        padding: 4px 0;
    }

    .mp-empty {
        padding: 16px;
        font-size: .85em;
        color: var(--text-faint);
        text-align: center;

        .monitor-spinner {
            display: inline-block;
            vertical-align: -1px;
            margin-right: 6px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 1.5px solid var(--border);
            border-top-color: var(--border-strong);
            animation: spin .8s linear infinite;
        }
    }

    .mp-all-good {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 22px 24px 20px;
        text-align: center;

        .mp-all-good-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 44px;
            height: 44px;
            margin-bottom: 12px;
            border-radius: 50%;
            background-color: var(--success-bg);
            box-shadow: 0 0 0 6px color-mix(in srgb, var(--success-bg) 60%, transparent);
            color: $ok;
            animation: check-pop .3s cubic-bezier(.34, 1.56, .64, 1);

            svg {
                width: 22px;
                height: 22px;
            }
        }

        .mp-all-good-title {
            font-size: .95em;
            font-weight: 600;
            color: var(--text);
        }

        .mp-all-good-text {
            margin-top: 2px;
            font-size: .8em;
            color: var(--text-faint);
        }

        .mp-all-good-time {
            margin-top: 10px;
            font-size: .72em;
            color: var(--text-disabled);
        }
    }

    .mp-group + .mp-group {
        border-top: 1px solid var(--tint-brand-bg);
    }

    .mp-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;

        &.mp-item-link {
            cursor: pointer;
            transition: background-color .1s ease;

            &:hover {
                background-color: var(--tint-brand-bg);

                .mp-brand-stack .channel-logo {
                    box-shadow: 0 0 0 2px var(--tint-brand-bg);
                }
            }
        }

        &.is-expanded {
            background-color: var(--tint-brand-soft);

            .mp-brand-stack .channel-logo {
                box-shadow: 0 0 0 2px var(--tint-brand-soft);
            }

            .mp-tag-chevron {
                transform: rotate(180deg);
            }
        }
    }

    .mp-sev {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;

        &.mp-sev-critical { background-color: $critical; }
        &.mp-sev-warning { background-color: $warning; }
        &.mp-sev-info { background-color: $info; }
    }

    .mp-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;

        .mp-label {
            font-size: .88em;
            font-weight: 600;
            color: var(--text);
        }

        .mp-desc {
            font-size: .78em;
            color: var(--text-faint);

            .mp-channel {
                margin-right: 6px;
                vertical-align: -2px;
            }
        }

        // Pilha de marcas: dá para saber quais canais estão no grupo sem expandir
        // (mesmo desenho do grupo de inativos na lista de canais).
        .mp-brand-stack {
            display: inline-flex;
            align-items: center;
            margin-right: 6px;
            vertical-align: -3px;

            .channel-logo {
                border-radius: 4px;
                box-shadow: 0 0 0 2px var(--surface-1);

                & + .channel-logo {
                    margin-left: -3px;
                }
            }

            .mp-brand-stack-more {
                margin-left: 5px;
                font-weight: 600;
                color: var(--text-faint);
            }
        }

        .mp-item-actions {
            margin-top: 6px;
        }
    }

    .mp-tag {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
        padding: 3px 9px;
        border-radius: 20px;
        font-size: .75em;
        font-weight: 700;
        white-space: nowrap;

        &.mp-tag-critical { background-color: var(--danger-bg); color: var(--monitor-critical-fg); }
        &.mp-tag-warning { background-color: var(--warning-bg); color: var(--monitor-warning-fg); }
        &.mp-tag-info { background-color: var(--info-bg); color: var(--monitor-info-fg); }

        .mp-tag-chevron {
            font-size: .85em;
            transition: transform .15s ease;
        }
    }

    .mp-subitems {
        padding: 2px 16px 8px 36px;
        background-color: var(--tint-brand-soft);

        .mp-subitem {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 7px 0;
            border-top: 1px dashed $border;

            .mp-subitem-message {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                font-size: .8em;
                color: var(--text-soft);

                .mp-channel {
                    margin-top: 2px;
                }
            }

            .mp-subitem-actions {
                display: flex;
                flex-shrink: 0;
            }
        }
    }

    .btn-sm {
        font-size: .72em;
        padding: 2px 8px;
    }

    .mp-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 9px 16px;
        border-top: 1px solid $border;
        background-color: var(--surface-2);
        font-size: .75em;
        color: var(--text-faint);

        .mp-foot-link {
            color: $brand;
            font-weight: 600;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}
</style>
